import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  FormControl,
  MenuItem,
  Select,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
  OutlinedInput,
  TextField,
  Switch,
  FormHelperText,
  Tooltip,
  Autocomplete,
  Radio,
  RadioGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect, useState } from "react";
import { Theme, useTheme } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientAllUsersRequest,
  getClientAllUsersSuccess,
  getTenantAllUsersRequest,
} from "../../../../redux/clients/slice";
import CancelIcon from "@mui/icons-material/Cancel";
import Loading from "../../../../components/loading/loading";
import {
  getInspectionFirmAllUsersRequest,
  getInspectionFirmAllUsersSuccess,
} from "../../../../redux/inspection-firms/slice";
import {
  getCreateIssueSearchOrderRequest,
  getOrderIssuesRequest,
} from "../../../../redux/orders/slice";
import { addOrderIssueRequest } from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import eventBus from "../../../../utils/EventBus";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name: string, selected: readonly string[], theme: Theme) {
  return {
    fontWeight: selected.includes(name)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

export default function AddIssueDialog({
  orders,
  showDialog,
  closeDialog,
}: any) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [searchText, setSearchText] = useState("");
  const [orderOptions, setOrderOptions] = useState<string[]>([]);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [isSameClient, setIsSameClient] = useState(false);
  const [isSameInspectionFirms, setIsSameInspectionFirms] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { createIssueSearchResults } = useSelector(
    (state: any) => state.orders
  );
  const { loading, tenantAllUsers, clientAllUsers } = useSelector(
    (state: any) => state.clients
  );
  const { inspectionFirmAllUsers } = useSelector(
    (state: any) => state.inspectionFirms
  );

  const validationSchema = Yup.object({
    type: Yup.string().required("This field can't be empty"),
    subType: Yup.string().required("This field can't be empty"),
    orderIds: Yup.array().min(1, "This field can't be empty"),
    orderType: Yup.string(),
    tenantUserIds: Yup.array()
      .of(Yup.string())
      .when("tenantCheckbox", {
        is: (value: boolean) => value,
        then: (schema) => schema.min(1, "This field can't be empty"),
        otherwise: (schema) => schema.notRequired(),
      }),
    clientUserIds: Yup.array()
      .of(Yup.string())
      .when(["clientCheckbox", "type"], {
        is: (clientCheckbox: boolean, type: string) =>
          clientCheckbox && type === "Client",
        then: (schema) => schema.min(1, "This field can't be empty"),
        otherwise: (schema) => schema.notRequired(),
      }),
    clientManager: Yup.boolean(),
    clientRequestor: Yup.boolean(),
    inspectorUserIds: Yup.array()
      .of(Yup.string())
      .when(["inspectorCheckbox", "type"], {
        is: (inspectorCheckbox: boolean, type: string) =>
          inspectorCheckbox && type === "Inspector",
        then: (schema) => schema.min(1, "This field can't be empty"),
        otherwise: (schema) => schema.notRequired(),
      }),
    inspectorManager: Yup.boolean(),
    description: Yup.string().required("This field can't be empty"),
    priority: Yup.string().required("This field can't be empty"),
    onHoldUntilResolved: Yup.boolean(),
    autoCloseIssue: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      type: "Tenant",
      subType: "",
      orderIds: [] as string[],
      orderType: "individual",
      tenantUserIds: [] as string[],
      tenantCheckbox: true,
      clientUserIds: [] as string[],
      clientCheckbox: false,
      clientManager: false,
      clientRequestor: false,
      inspectorUserIds: [] as string[],
      inspectorCheckbox: false,
      inspectorManager: false,
      description: "",
      priority: "",
      onHoldUntilResolved: true,
      autoCloseIssue: false,
    },
    validationSchema,
    validateOnChange: true,
    onSubmit: (values) => {
      const payload: any = {
        subType: values.subType,
        orderIds: values.orderIds,
        tenantUserIds: values.tenantCheckbox ? values.tenantUserIds : [],
        description: values.description,
        priority: values.priority,
        onHoldUntilResolved: values.onHoldUntilResolved,
        isResolved: values.autoCloseIssue,
        createIndividualIssues:
          values.orderType === "individual" ? true : false,
      };
      if (values.type === "Client") {
        payload.bulkIssueAssigneeTypes = [];
        payload.clientUserIds = values.clientCheckbox
          ? values.clientUserIds
          : [];
        if (values.clientManager) {
          payload.bulkIssueAssigneeTypes.push("ClientAccountManager");
        }
        if (values.clientRequestor) {
          payload.bulkIssueAssigneeTypes.push("Requestor");
        }
      }
      if (values.type === "Inspector") {
        payload.bulkIssueAssigneeTypes = [];
        payload.inspectorUserIds = values.inspectorCheckbox
          ? values.inspectorUserIds
          : [];
        if (values.inspectorManager) {
          payload.bulkIssueAssigneeTypes.push("InspectorAccountManager");
        }
      }
      addIssue(payload);
    },
  });

  useEffect(() => {
    dispatch(getInspectionFirmAllUsersSuccess({ response: null }));
    dispatch(getClientAllUsersSuccess({ response: null }));
    dispatch(getTenantAllUsersRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
    if (orders) {
      const reducedOrders = orders.map((order: any) =>
        order?.orderNumber.toString()
      );
      setSelectedOrderNumber(reducedOrders);
      setSelectedOrders(orders);
    }
  }, [orders]);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      if (searchText) {
        dispatch(
          getCreateIssueSearchOrderRequest({
            searchTerm: searchText.trim(),
          })
        );
      } else {
        setOrderOptions([]);
      }
    }, 500);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchText]); // eslint-disable-line

  useEffect(() => {
    if (createIssueSearchResults) {
      const reducedOrders = createIssueSearchResults.map((order: any) =>
        order?.orderNumber.toString()
      );
      setOrderOptions(reducedOrders);
    }
  }, [createIssueSearchResults]);

  useEffect(() => {
    const orderIds = selectedOrders?.map((order) => order?.id);
    formik.setFieldValue("orderIds", orderIds);
    const type = formik.values.type;
    if (type === "Client") {
      const isSame = checkSameClient();
      if (isSame && selectedOrders.length > 0) {
        dispatch(
          getClientAllUsersRequest({ id: selectedOrders[0]?.client?.id })
        );
      }
      formik.setFieldValue("clientCheckbox", isSame ? true : false);
    } else if (type === "Inspector") {
      const isSame = checkSameInspector();
      if (isSame && selectedOrders.length > 0) {
        dispatch(
          getInspectionFirmAllUsersRequest({
            id: selectedOrders[0]?.inspectorFirm?.id,
          })
        );
      }
      formik.setFieldValue("inspectorCheckbox", isSame ? true : false);
    }
  }, [formik.values.type, selectedOrders]); // eslint-disable-line

  const getSubIssueTypes = () => {
    if (formik.values.type === "Tenant") {
      return [
        { label: "Internal", value: "Internal" },
        { label: "Payment", value: "Payment" },
        { label: "QC", value: "Qc" },
      ];
    } else if (formik.values.type === "Client") {
      return [
        { label: "Contact", value: "Contact" },
        { label: "Recruiting", value: "Recruiting" },
        { label: "Update", value: "Update" },
      ];
    } else if (formik.values.type === "Inspector") {
      return [
        { label: "Contact", value: "InspectorContact" },
        { label: "Returned", value: "Returned" },
        { label: "Acceptance", value: "Acceptance" },
      ];
    }
  };

  const getTenantUserFullName = (id: string) => {
    const filterUser = tenantAllUsers?.filter((user: any) => user.id === id);
    return filterUser && filterUser.length > 0 && filterUser[0].fullName;
  };

  const deleteTenantUser = (id: string) => {
    const filteredUsers = formik.values.tenantUserIds.filter(
      (value) => value !== id
    );
    formik.setFieldValue("tenantUserIds", filteredUsers);
  };

  const getClientUserFullName = (id: string) => {
    const filterUser = clientAllUsers?.filter((user: any) => user.id === id);
    return filterUser && filterUser.length > 0 && filterUser[0].fullName;
  };

  const deleteClientUser = (id: string) => {
    const filteredUsers = formik.values.clientUserIds.filter(
      (value) => value !== id
    );
    formik.setFieldValue("clientUserIds", filteredUsers);
  };

  const getInspectorUserFullName = (id: string) => {
    const filterUser = inspectionFirmAllUsers?.filter(
      (user: any) => user.id === id
    );
    return filterUser && filterUser.length > 0 && filterUser[0].fullName;
  };

  const deleteInspectorUser = (id: string) => {
    const filteredUsers = formik.values.inspectorUserIds.filter(
      (value) => value !== id
    );
    formik.setFieldValue("inspectorUserIds", filteredUsers);
  };

  const addIssue = async (payload: any) => {
    setShowLoading(true);
    const response = await addOrderIssueRequest(payload);
    setShowLoading(false);
    if (response?.status === 201) {
      eventBus.emit("createIssuesSuccess");
      dispatch(
        getOrderIssuesRequest({
          page: 1,
          pageSize: 10,
          sortColumn: "is_resolved",
          sortOrder: "asc",
        })
      );
      closeDialog();
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const checkSameClient = () => {
    let isSame = false;
    const uniqueClients = selectedOrders.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t?.client?.id === item?.client?.id)
    );
    if (uniqueClients.length > 1) {
      isSame = false;
    } else {
      isSame = true;
    }
    setIsSameClient(isSame);
    return isSame;
  };

  const checkSameInspector = () => {
    let isSame = false;
    const uniqueInspectionFirms = selectedOrders.filter(
      (item, index, self) =>
        index ===
        self.findIndex((t) => t?.inspectorFirm?.id === item?.inspectorFirm?.id)
    );
    if (uniqueInspectionFirms.length > 1) {
      isSame = false;
    } else {
      isSame = true;
    }
    setIsSameInspectionFirms(isSame);
    return isSame;
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Dialog
          open={showDialog}
          onClose={closeDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth={true}
          maxWidth={"lg"}
        >
          <DialogTitle id="responsive-dialog-title">
            <Typography variant="h5" color="black">
              Add Issue
            </Typography>
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: "black",
            })}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent dividers>
            <DialogContentText id="alert-dialog-description">
              <Box>
                <Box className="display-name-layout">
                  <Typography className="label-txt">Issue Type</Typography>
                  <FormControl fullWidth>
                    <Select
                      labelId="issue-type-label"
                      id="issue-type"
                      name="type"
                      value={formik.values.type}
                      onChange={(e) => {
                        formik.handleChange(e);
                        formik.setFieldValue("subType", "");
                      }}
                    >
                      <MenuItem value={"Tenant"}>Internal Issue</MenuItem>
                      <MenuItem value={"Client"}>Client Issue</MenuItem>
                      <MenuItem value={"Inspector"}>Inspector Issue</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Typography className="label-txt">Issue Sub Type</Typography>
                  <FormControl
                    fullWidth
                    error={
                      formik.touched.subType && Boolean(formik.errors.subType)
                    }
                  >
                    <Select
                      labelId="issue-subType-label"
                      id="issue-subType"
                      name="subType"
                      value={formik.values.subType}
                      onChange={formik.handleChange}
                    >
                      {getSubIssueTypes()?.map((subIssue: any) => (
                        <MenuItem value={subIssue?.value}>
                          {subIssue?.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {formik.touched.subType && formik.errors.subType}
                    </FormHelperText>
                  </FormControl>
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Typography className="label-txt">Selected Orders</Typography>
                  <FormControl
                    fullWidth
                    error={
                      (formik.touched.orderIds || formik.submitCount > 0) &&
                      Boolean(formik.errors.orderIds)
                    }
                  >
                    <Autocomplete
                      id="order-numbers"
                      multiple
                      options={orderOptions}
                      value={selectedOrderNumber}
                      onChange={(event, newValue) => {
                        setSelectedOrderNumber(newValue);
                        let allOrders = [...selectedOrders];
                        if (createIssueSearchResults) {
                          allOrders = [
                            ...selectedOrders,
                            ...createIssueSearchResults,
                          ];
                        }
                        const uniqueOrders = Array.from(
                          new Map(
                            allOrders.map((item) => [item.id, item])
                          ).values()
                        );
                        const filterOrderNumbers = newValue.map(Number);
                        const filtered = uniqueOrders.filter((order) =>
                          filterOrderNumbers.includes(order.orderNumber)
                        );
                        setSelectedOrders(filtered);
                        setOrderOptions([]);
                      }}
                      freeSolo
                      renderTags={(value: readonly string[], getTagProps) =>
                        value.map((option: string, index: number) => (
                          <Chip
                            sx={{ height: 28, borderWidth: 1.5 }}
                            variant="outlined"
                            label={option}
                            color="primary"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                      )}
                    />
                    <FormHelperText>
                      {(formik.touched.orderIds || formik.submitCount > 0) &&
                        formik.errors.orderIds}
                    </FormHelperText>

                    {selectedOrderNumber.length > 1 && (
                      <RadioGroup
                        sx={{ mt: 1, color: "rgba(0, 0, 0, 0.87)" }}
                        aria-labelledby="demo-radio-buttons-group-label"
                        name="orderType"
                        value={formik.values.orderType}
                        onChange={formik.handleChange}
                      >
                        <FormControlLabel
                          value="individual"
                          control={<Radio />}
                          label="Create individual issues for each selected order"
                        />
                        <FormControlLabel
                          value="group"
                          control={<Radio />}
                          label="Attach all selected orders (with the same Inspector or client) to a single issue "
                        />
                      </RadioGroup>
                    )}
                  </FormControl>
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Box
                    sx={{
                      minWidth: 192,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography fontSize={12} color="rgba(0, 0, 0, 0.6)">
                      Assigned Users
                    </Typography>
                    <Tooltip
                      title="Assigned users will see this issue in their dashboard and issues queue. Assigned users will be able to resolve the issue and clear the flag."
                      arrow
                      placement="top"
                    >
                      <IconButton sx={{ p: 0, mr: 2 }}>
                        <InfoOutlinedIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box width={"100%"}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="tenantCheckbox"
                            checked={formik.values.tenantCheckbox}
                            onChange={formik.handleChange}
                          />
                        }
                        label="Internal Users"
                        sx={{ width: 200 }}
                      />
                      <FormControl
                        fullWidth
                        error={
                          (formik.touched.tenantUserIds ||
                            formik.submitCount > 0) &&
                          Boolean(formik.errors.tenantUserIds)
                        }
                      >
                        <Select
                          labelId="demo-multiple-chip-label"
                          id="demo-multiple-chip"
                          multiple
                          name="tenantUserIds"
                          value={formik.values.tenantUserIds}
                          onChange={formik.handleChange}
                          input={<OutlinedInput id="select-multiple-chip" />}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => (
                                <Chip
                                  key={value}
                                  variant="outlined"
                                  label={getTenantUserFullName(value)}
                                  color="primary"
                                  size="small"
                                  onDelete={(e) => deleteTenantUser(value)}
                                  deleteIcon={
                                    <IconButton
                                      onMouseDown={(e) => e.stopPropagation()}
                                      size="small"
                                      sx={{ padding: 0.5 }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  }
                                />
                              ))}
                            </Box>
                          )}
                          MenuProps={MenuProps}
                        >
                          {tenantAllUsers?.map((user: any) => (
                            <MenuItem
                              key={user.id}
                              value={user.id}
                              style={getStyles(
                                user.fullName,
                                formik.values.tenantUserIds,
                                theme
                              )}
                            >
                              {user.fullName}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {(formik.touched.tenantUserIds ||
                            formik.submitCount > 0) &&
                            formik.errors.tenantUserIds}
                        </FormHelperText>
                      </FormControl>
                    </Box>

                    {formik.values.type === "Client" && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 2 }}
                      >
                        {isSameClient ? (
                          <>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="clientCheckbox"
                                  checked={formik.values.clientCheckbox}
                                  onChange={formik.handleChange}
                                />
                              }
                              label="Client"
                              sx={{ width: 200 }}
                            />
                            <FormControl
                              fullWidth
                              error={
                                (formik.touched.clientUserIds ||
                                  formik.submitCount > 0) &&
                                Boolean(formik.errors.clientUserIds)
                              }
                            >
                              <Select
                                labelId="demo-multiple-chip-label"
                                id="demo-multiple-chip"
                                multiple
                                name="clientUserIds"
                                value={formik.values.clientUserIds}
                                onChange={formik.handleChange}
                                input={
                                  <OutlinedInput id="select-multiple-chip" />
                                }
                                renderValue={(selected) => (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    {selected.map((value) => (
                                      <Chip
                                        key={value}
                                        variant="outlined"
                                        label={getClientUserFullName(value)}
                                        color="primary"
                                        size="small"
                                        onDelete={(e) =>
                                          deleteClientUser(value)
                                        }
                                        deleteIcon={
                                          <IconButton
                                            onMouseDown={(e) =>
                                              e.stopPropagation()
                                            }
                                            size="small"
                                            sx={{ padding: 0.5 }}
                                          >
                                            <CancelIcon fontSize="small" />
                                          </IconButton>
                                        }
                                      />
                                    ))}
                                  </Box>
                                )}
                                MenuProps={MenuProps}
                              >
                                {clientAllUsers?.map((user: any) => (
                                  <MenuItem
                                    key={user.id}
                                    value={user.id}
                                    style={getStyles(
                                      user.fullName,
                                      formik.values.clientUserIds,
                                      theme
                                    )}
                                  >
                                    {user.fullName}
                                  </MenuItem>
                                ))}
                              </Select>

                              <FormHelperText>
                                {(formik.touched.clientUserIds ||
                                  formik.submitCount > 0) &&
                                  formik.errors.clientUserIds}
                              </FormHelperText>
                            </FormControl>
                          </>
                        ) : (
                          <Box display={"grid"}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="clientManager"
                                  checked={formik.values.clientManager}
                                  onChange={formik.handleChange}
                                />
                              }
                              label="Client Manager"
                              sx={{ width: 200 }}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="clientRequestor"
                                  checked={formik.values.clientRequestor}
                                  onChange={formik.handleChange}
                                />
                              }
                              label="Requestor"
                              sx={{ width: 200 }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                    {formik.values.type === "Inspector" && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 2 }}
                      >
                        {isSameInspectionFirms ? (
                          <>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="inspectorCheckbox"
                                  checked={formik.values.inspectorCheckbox}
                                  onChange={formik.handleChange}
                                />
                              }
                              label="Inspector"
                              sx={{ width: 200 }}
                            />
                            <FormControl
                              fullWidth
                              error={
                                (formik.touched.inspectorUserIds ||
                                  formik.submitCount > 0) &&
                                Boolean(formik.errors.inspectorUserIds)
                              }
                            >
                              <Select
                                labelId="demo-multiple-chip-label"
                                id="demo-multiple-chip"
                                multiple
                                name="inspectorUserIds"
                                value={formik.values.inspectorUserIds}
                                onChange={formik.handleChange}
                                input={
                                  <OutlinedInput id="select-multiple-chip" />
                                }
                                renderValue={(selected) => (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    {selected.map((value) => (
                                      <Chip
                                        key={value}
                                        variant="outlined"
                                        label={getInspectorUserFullName(value)}
                                        color="primary"
                                        size="small"
                                        onDelete={(e) =>
                                          deleteInspectorUser(value)
                                        }
                                        deleteIcon={
                                          <IconButton
                                            onMouseDown={(e) =>
                                              e.stopPropagation()
                                            }
                                            size="small"
                                            sx={{ padding: 0.5 }}
                                          >
                                            <CancelIcon fontSize="small" />
                                          </IconButton>
                                        }
                                      />
                                    ))}
                                  </Box>
                                )}
                                MenuProps={MenuProps}
                              >
                                {inspectionFirmAllUsers?.map((user: any) => (
                                  <MenuItem
                                    key={user.id}
                                    value={user.id}
                                    style={getStyles(
                                      user.fullName,
                                      formik.values.inspectorUserIds,
                                      theme
                                    )}
                                  >
                                    {user.fullName}
                                  </MenuItem>
                                ))}
                              </Select>

                              <FormHelperText>
                                {(formik.touched.inspectorUserIds ||
                                  formik.submitCount > 0) &&
                                  formik.errors.inspectorUserIds}
                              </FormHelperText>
                            </FormControl>
                          </>
                        ) : (
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="inspectorManager"
                                checked={formik.values.inspectorManager}
                                onChange={formik.handleChange}
                              />
                            }
                            label="Inspector Manager"
                            sx={{ width: 200 }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Typography className="label-txt">Issue</Typography>
                  <TextField
                    name="description"
                    fullWidth
                    id="outlined-basic"
                    label="Describe the Issue"
                    variant="outlined"
                    multiline
                    onChange={formik.handleChange}
                    value={formik.values.description}
                    rows={2}
                    error={
                      (formik.touched.description || formik.submitCount > 0) &&
                      Boolean(formik.errors.description)
                    }
                    helperText={
                      (formik.touched.description || formik.submitCount > 0) &&
                      formik.errors.description
                    }
                  />
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Typography className="label-txt">Priority</Typography>
                  <FormControl
                    fullWidth
                    error={
                      formik.touched.priority && Boolean(formik.errors.priority)
                    }
                  >
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      name="priority"
                      value={formik.values.priority}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value={"Low"}>Low</MenuItem>
                      <MenuItem value={"Medium"}>Medium</MenuItem>
                      <MenuItem value={"High"}>High</MenuItem>
                    </Select>
                    <FormHelperText>
                      {formik.touched.priority && formik.errors.priority}
                    </FormHelperText>
                  </FormControl>
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Typography className="label-txt">
                    Auto-Close Issue?
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        name="autoCloseIssue"
                        checked={formik.values.autoCloseIssue}
                        onChange={formik.handleChange}
                      />
                    }
                    label={formik.values.autoCloseIssue ? "Yes" : "No"}
                  />
                </Box>
                <Divider />
                <Box className="user-name-layout">
                  <Typography className="label-txt">
                    Put Job on Hold Until <br /> Resolved?
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        name="onHoldUntilResolved"
                        checked={formik.values.onHoldUntilResolved}
                        onChange={formik.handleChange}
                      />
                    }
                    label={formik.values.onHoldUntilResolved ? "Yes" : "No"}
                  />
                </Box>
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closeDialog} type="reset">
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                formik.handleSubmit();
              }}
              disabled={!formik.isValid}
            >
              Create Issue
            </Button>
          </DialogActions>
        </Dialog>
      </form>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {(loading || showLoading) && <Loading />}
    </>
  );
}
